"use client";

import * as React from "react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const COUNTRY_CODES = [
  { code: "+1",   country: "US", flag: "🇺🇸", label: "United States" },
  { code: "+44",  country: "GB", flag: "🇬🇧", label: "United Kingdom" },
  { code: "+91",  country: "IN", flag: "🇮🇳", label: "India" },
  { code: "+971", country: "AE", flag: "🇦🇪", label: "UAE" },
  { code: "+65",  country: "SG", flag: "🇸🇬", label: "Singapore" },
  { code: "+61",  country: "AU", flag: "🇦🇺", label: "Australia" },
  { code: "+49",  country: "DE", flag: "🇩🇪", label: "Germany" },
  { code: "+33",  country: "FR", flag: "🇫🇷", label: "France" },
  { code: "+81",  country: "JP", flag: "🇯🇵", label: "Japan" },
  { code: "+82",  country: "KR", flag: "🇰🇷", label: "South Korea" },
  { code: "+86",  country: "CN", flag: "🇨🇳", label: "China" },
  { code: "+55",  country: "BR", flag: "🇧🇷", label: "Brazil" },
  { code: "+52",  country: "MX", flag: "🇲🇽", label: "Mexico" },
  { code: "+27",  country: "ZA", flag: "🇿🇦", label: "South Africa" },
  { code: "+234", country: "NG", flag: "🇳🇬", label: "Nigeria" },
  { code: "+971", country: "AE", flag: "🇦🇪", label: "Dubai / UAE" },
  { code: "+966", country: "SA", flag: "🇸🇦", label: "Saudi Arabia" },
  { code: "+60",  country: "MY", flag: "🇲🇾", label: "Malaysia" },
  { code: "+62",  country: "ID", flag: "🇮🇩", label: "Indonesia" },
  { code: "+63",  country: "PH", flag: "🇵🇭", label: "Philippines" },
  { code: "+64",  country: "NZ", flag: "🇳🇿", label: "New Zealand" },
  { code: "+353", country: "IE", flag: "🇮🇪", label: "Ireland" },
  { code: "+31",  country: "NL", flag: "🇳🇱", label: "Netherlands" },
  { code: "+46",  country: "SE", flag: "🇸🇪", label: "Sweden" },
  { code: "+41",  country: "CH", flag: "🇨🇭", label: "Switzerland" },
];

interface PhoneInputProps {
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  className?: string;
}

export function PhoneInput({
  value = "",
  onChange,
  onBlur,
  disabled,
  className,
}: PhoneInputProps) {
  // Parse existing value into country code + number
  const parseValue = (val: string) => {
    for (const c of COUNTRY_CODES.sort((a, b) => b.code.length - a.code.length)) {
      if (val.startsWith(c.code + " ")) {
        return { dialCode: c.code, number: val.slice(c.code.length + 1) };
      }
      if (val.startsWith(c.code)) {
        return { dialCode: c.code, number: val.slice(c.code.length) };
      }
    }
    return { dialCode: "+91", number: val };
  };

  const parsed = parseValue(value);
  const [dialCode, setDialCode] = React.useState(parsed.dialCode);
  const [number, setNumber] = React.useState(parsed.number);

  const handleDialCodeChange = (code: string) => {
    setDialCode(code);
    onChange?.(number ? `${code} ${number}` : code);
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const num = e.target.value.replace(/[^\d\s\-()]/g, "");
    setNumber(num);
    onChange?.(num ? `${dialCode} ${num}` : "");
  };

  return (
    <div className={cn("flex gap-2", className)}>
      {/* Country Code Dropdown */}
      <Select value={dialCode} onValueChange={handleDialCodeChange} disabled={disabled}>
        <SelectTrigger className="w-[110px] shrink-0 font-medium">
          <SelectValue>
            {(() => {
              const found = COUNTRY_CODES.find((c) => c.code === dialCode);
              return found ? (
                <span className="flex items-center gap-1.5">
                  <span>{found.flag}</span>
                  <span className="text-sm">{found.code}</span>
                </span>
              ) : (
                <span>{dialCode}</span>
              );
            })()}
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="max-h-[260px]">
          {COUNTRY_CODES.filter(
            (c, i, arr) => arr.findIndex((x) => x.code === c.code && x.country === c.country) === i
          ).map((c) => (
            <SelectItem key={`${c.country}-${c.code}`} value={c.code}>
              <span className="flex items-center gap-2">
                <span>{c.flag}</span>
                <span className="text-sm font-medium">{c.code}</span>
                <span className="text-xs text-muted-foreground truncate max-w-[120px]">{c.label}</span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Phone Number Input */}
      <Input
        type="tel"
        placeholder="98765 43210"
        value={number}
        onChange={handleNumberChange}
        onBlur={onBlur}
        disabled={disabled}
        className="flex-1"
      />
    </div>
  );
}
